/**
 * Date helpers for the Wiseguy session picker.
 *
 * Their own module rather than locals in `WiseguyAlertsWidget`: exporting
 * functions from a component file breaks React Fast Refresh, and these need to
 * be exported to be tested. Same split as `alertStore` and `tickerChrome`.
 */

/**
 * How far back the session picker reaches, in calendar days.
 *
 * Not `SEARCH_LOOKBACK_DAYS` (365): that governs the ticker-search path, which
 * is a different query with a different row cap. 90 days is the window we have
 * actually exercised against `/v1/steamroom/alerts/history`.
 */
export const SESSION_PICKER_LOOKBACK_DAYS = 90;

/**
 * True for a Saturday or Sunday, given an ET `YYYY-MM-DD`.
 *
 * ⚠️ Anchored at **noon UTC**, not `new Date(ymd)`. A bare date string parses
 * as UTC midnight, which anywhere west of Greenwich is the previous calendar
 * day — so Monday 2026-08-31 would read as Sunday and get disabled for every
 * user in ET. Noon is far enough from both midnights that no timezone in use
 * can shift the day. `sessionDate.test.ts` fails on seven cases if this is
 * "simplified" back.
 *
 * ⚠️ Weekends only. Market holidays are NOT covered: nothing in the app, in QF
 * (`/v1/calendar/` serves earnings and economic only), or in `fznService`
 * publishes a trading calendar, and a hardcoded holiday list goes stale every
 * year and then fails silently. A holiday stays selectable and returns an
 * empty tape, which is honest — there were no alerts. Swap this for a real
 * calendar when an endpoint exists.
 */
export function isWeekend(ymd: string): boolean {
  const day = new Date(`${ymd}T12:00:00Z`).getUTCDay();
  return day === 0 || day === 6;
}

/**
 * The most recent weekday at or before `ymd`.
 *
 * Used when a weekend is picked: offer the Friday rather than refuse silently.
 * Steps back at most two days, so it cannot loop.
 */
export function lastWeekdayOnOrBefore(ymd: string): string {
  let cursor = ymd;
  for (let i = 0; i < 3 && isWeekend(cursor); i += 1) {
    const d = new Date(`${cursor}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 1);
    cursor = d.toISOString().slice(0, 10);
  }
  return cursor;
}
